import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  useUpdateChargingTimerMutation,
  useViewChargingTimerQuery,
} from "../../../../../redux/api/ev-charger/evChargerAPI";
import { IHttpError } from "../../../../../shared/oversight-core/interfaces/http-errror";
import ITimeSettingItem from "../../../../../shared/oversight-core/interfaces/time-setting-item";
import AppToggle from "../../../../../shared/oversight-core/ui-elements/app-toggle/app-toggle";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import SpinnerModal from "../../../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  isoToTimeSettingItem,
  timeSettingItemToIso,
} from "../../../../../shared/oversight-core/utils/time-utils";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../../shared/oversight-core/utils/toast";
import styles from "./change-time-settings-modal.module.scss";

interface ITimeSettingsData {
  startTime: ITimeSettingItem;
  endTime: ITimeSettingItem;
}

interface IProps extends ModelContainerProps {
  show: boolean;
  evChargerId?: string;
  initialData?: Partial<ITimeSettingsData>;
}

const defaultStartTime: ITimeSettingItem = {
  hour: "08",
  minute: "00",
  period: "AM",
  enabled: true,
};

const defaultEndTime: ITimeSettingItem = {
  hour: "09",
  minute: "00",
  period: "AM",
  enabled: true,
};

const ChangeTimeSettingsModal = (props: IProps) => {
  const { show, evChargerId, onClose, initialData, ...rest } = props;

  const [startTime, setStartTime] = useState<ITimeSettingItem>({
    ...defaultStartTime,
    ...initialData?.startTime,
  });

  const [endTime, setEndTime] = useState<ITimeSettingItem>({
    ...defaultEndTime,
    ...initialData?.endTime,
  });

  const [activeTarget, setActiveTarget] = useState<"start" | "end">("end");
  const [activeField, setActiveField] = useState<"hour" | "minute">("minute");

  const { data: timerData, isFetching: isFetchingTimer } =
    useViewChargingTimerQuery(
      { evChargerId: evChargerId || "" },
      { skip: !evChargerId || !show },
    );

  const [updateChargingTimer, { isLoading: isUpdatingTimer }] =
    useUpdateChargingTimerMutation();

  useEffect(() => {
    if (show && timerData) {
      setStartTime(isoToTimeSettingItem(timerData.startTime));
      setEndTime(isoToTimeSettingItem(timerData.endTime));
      setActiveTarget("end");
      setActiveField("minute");
    } else if (show && initialData) {
      if (initialData.startTime) {
        setStartTime({ ...defaultStartTime, ...initialData.startTime });
      }
      if (initialData.endTime) {
        setEndTime({ ...defaultEndTime, ...initialData.endTime });
      }
      setActiveTarget("end");
      setActiveField("minute");
    }
  }, [show, timerData, initialData]);

  const currentItem = activeTarget === "start" ? startTime : endTime;

  const updateCurrentItem = (updates: Partial<ITimeSettingItem>) => {
    if (activeTarget === "start") {
      setStartTime((prev) => ({ ...prev, ...updates }));
    } else {
      setEndTime((prev) => ({ ...prev, ...updates }));
    }
  };

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = e.target.value.replace(/\D/g, "");
    if (rawVal === "") {
      updateCurrentItem({ hour: "" });
      return;
    }
    let num = parseInt(rawVal, 10);
    if (num > 12) num = 12;
    updateCurrentItem({
      hour: rawVal.length === 1 ? rawVal : num.toString().padStart(2, "0"),
    });
  };

  const handleHourBlur = () => {
    if (!currentItem.hour || parseInt(currentItem.hour, 10) === 0) {
      updateCurrentItem({ hour: "12" });
    } else {
      const num = parseInt(currentItem.hour, 10);
      updateCurrentItem({ hour: num.toString().padStart(2, "0") });
    }
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = e.target.value.replace(/\D/g, "");
    if (rawVal === "") {
      updateCurrentItem({ minute: "" });
      return;
    }
    let num = parseInt(rawVal, 10);
    if (num > 59) num = 59;
    updateCurrentItem({
      minute: rawVal.length === 1 ? rawVal : num.toString().padStart(2, "0"),
    });
  };

  const handleMinuteBlur = () => {
    if (!currentItem.minute) {
      updateCurrentItem({ minute: "00" });
    } else {
      const num = parseInt(currentItem.minute, 10);
      updateCurrentItem({ minute: num.toString().padStart(2, "0") });
    }
  };

  const handleConfirmAction = async () => {
    if (evChargerId) {
      const isoStartTime = timeSettingItemToIso(startTime);
      const isoEndTime = timeSettingItemToIso(endTime);

      try {
        await updateChargingTimer({
          evChargerId,
          startTime: isoStartTime,
          endTime: isoEndTime,
        }).unwrap();
        showSuccessMessage("Charging timer updated successfully");
        if (onClose) onClose();
      } catch (err) {
        const httpError = err as IHttpError;
        showErrorMessage(
          httpError?.message || "Failed to update charging timer",
        );
      }
    }
  };

  const formatTimeString = (item: ITimeSettingItem) => {
    const h = item.hour.padStart(2, "0");
    const m = item.minute.padStart(2, "0");
    return `${h}:${m} ${item.period}`;
  };

  return (
    <ModalContainer
      show={show}
      {...rest}
      onClose={onClose || props.onCancel}
      onCancel={props.onCancel || onClose}
      title="Change Time Settings"
      onConfirm={handleConfirmAction}
      confirmButtonText="Confirm"
      cancelButtonText="Cancel"
      confirmButtonVariant="blue"
      cancelButtonVariant="transparent"
      size="modal-md"
    >
      <>
        <Row className="justify-content-center mb-2">
          <Col className="col-12 col-sm-11 col-lg-10 col-xl-9">
            <Row className="align-items-center justify-content-between">
              <Col className={styles.timeLabelGroup}>
                <span className="text-dark font-weight-400">Start Time</span>
                <span
                  className={`${styles.rowTime} ${
                    activeTarget === "start" ? "text-primary" : "text-light"
                  }`}
                  onClick={() => setActiveTarget("start")}
                >
                  {formatTimeString(startTime)}
                </span>
              </Col>
              <Col className="col-auto">
                <AppToggle
                  isOn={startTime.enabled}
                  onSwitch={(isOn) =>
                    setStartTime((prev) => ({ ...prev, enabled: isOn }))
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <div className={styles.divider} />
        <Row className="justify-content-center mt-3">
          <Col className="col-12 col-sm-11 col-lg-10 col-xl-9">
            <Row className="align-items-center justify-content-between">
              <Col className={styles.timeLabelGroup}>
                <span className="text-dark font-weight-400">End Time</span>
                <span
                  className={`${styles.rowTime} ${
                    activeTarget === "end" ? "text-primary" : "text-light"
                  }`}
                  onClick={() => setActiveTarget("end")}
                >
                  {formatTimeString(endTime)}
                </span>
              </Col>
              <Col className="col-auto">
                <AppToggle
                  isOn={endTime.enabled}
                  onSwitch={(isOn) =>
                    setEndTime((prev) => ({ ...prev, enabled: isOn }))
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Center Time Picker Controls */}
        <div className={styles.timePickerSection}>
          <div className={styles.timeInputGroup}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={2}
              className={`${styles.timeInputBox} ${
                activeField === "hour" ? styles.timeInputBoxActive : ""
              }`}
              value={currentItem.hour}
              onFocus={() => setActiveField("hour")}
              onChange={handleHourChange}
              onBlur={handleHourBlur}
            />
            <span className={styles.colon}>:</span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={2}
              className={`${styles.timeInputBox} ${
                activeField === "minute" ? styles.timeInputBoxActive : ""
              }`}
              value={currentItem.minute}
              onFocus={() => setActiveField("minute")}
              onChange={handleMinuteChange}
              onBlur={handleMinuteBlur}
            />
          </div>

          {/* AM / PM Segmented Control */}
          <div className={styles.periodSegment}>
            <button
              type="button"
              className={`${styles.periodBtn} ${
                currentItem.period === "AM" ? styles.periodBtnActive : ""
              }`}
              onClick={() => updateCurrentItem({ period: "AM" })}
            >
              AM
            </button>
            <button
              type="button"
              className={`${styles.periodBtn} ${
                currentItem.period === "PM" ? styles.periodBtnActive : ""
              }`}
              onClick={() => updateCurrentItem({ period: "PM" })}
            >
              PM
            </button>
          </div>
        </div>
        <SpinnerModal show={isFetchingTimer || isUpdatingTimer} />
      </>
    </ModalContainer>
  );
};

export default ChangeTimeSettingsModal;
